import { plural, t } from '@lingui/core/macro';
import type { BrandInsightsLiveBookingRecord, BrandInsightsLiveRecord } from '@navio/nsdk';
import type { NoddiAsyncError } from 'noddi-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  NavioLiveDot,
  NoddiBadge,
  NoddiCard,
  NoddiSkeleton
} from 'noddi-ui-web';
import { formatDisplayDateTime } from 'noddi-util';

import { ApiErrorMessageWithTrans } from '../../components/Error/ApiErrorMessageWithTrans';
import { translateSalesItemCategory } from '../../utils/translateBookingCategory';
import { formatMoney } from './brandInsightsFormat';

interface Props {
  live?: BrandInsightsLiveRecord;
  error: NoddiAsyncError | null;
  isRetrying: boolean;
  onRetry: () => void;
}

/**
 * What is happening on the marketplace right now, folded to one line above the period figures:
 * today's totals and the newest booking, expanding to the latest bookings. Refreshes on its own and
 * ignores the page period.
 */
export const BrandInsightsLiveFeed = ({ live, error, isRetrying, onRetry }: Props) => {
  // A failed poll keeps the last answer on screen; only a failure with nothing cached replaces it.
  if (!live && error) {
    return (
      <NoddiCard size='small' title={t`Live bookings`}>
        <ApiErrorMessageWithTrans error={error} isRetrying={isRetrying} onRetry={onRetry} />
      </NoddiCard>
    );
  }

  const latest = live?.bookings[0];
  return (
    <NoddiCard size='small'>
      <Accordion>
        <AccordionItem value='live' className='border-b-0'>
          <AccordionTrigger className='items-center py-0 hover:no-underline'>
            <span className='flex min-w-0 flex-1 flex-wrap items-center gap-x-5 gap-y-2'>
              <NoddiBadge variant='success' className='gap-2'>
                <NavioLiveDot className='size-2.5' />
                {t`Live`}
              </NoddiBadge>
              {live ? (
                <span className='text-sm'>
                  <span className='text-muted-foreground'>{t`Today so far`}</span>{' '}
                  <span className='font-semibold tabular-nums'>
                    {plural(live.today.bookings, { one: '# booking', other: '# bookings' })} ·{' '}
                    {formatMoney(live.today.revenue)}
                  </span>
                </span>
              ) : (
                <NoddiSkeleton variant='button' className='h-5 w-56' />
              )}
              {latest && (
                <span className='hidden min-w-0 flex-1 truncate text-sm text-muted-foreground md:inline'>
                  {t`Latest`}: {describeBooking(latest)} · {formatMoney(latest.revenue)} ·{' '}
                  {formatDisplayDateTime(latest.createdAt)}
                </span>
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent className='flex flex-col gap-3 pt-4 pb-0'>
            <p className='text-xs text-muted-foreground'>{t`Updates continuously, whatever period is selected`}</p>
            {!live ? (
              <div className='flex flex-col gap-2'>
                <NoddiSkeleton className='h-8 w-full' />
                <NoddiSkeleton className='h-8 w-full' />
                <NoddiSkeleton className='h-8 w-full' />
              </div>
            ) : live.bookings.length > 0 ? (
              <ul className='grid gap-x-10 lg:grid-cols-2'>
                {live.bookings.map((booking, index) => (
                  // The record carries no booking id by design (public page); newest-first order keys the rows.
                  <li
                    key={`${booking.createdAt}-${index}`}
                    className='flex items-center gap-4 border-t border-border py-2'
                  >
                    <div className='flex min-w-0 flex-1 flex-col sm:flex-row sm:items-baseline sm:gap-2'>
                      <span className='truncate font-medium'>{describeBooking(booking)}</span>
                      <span className='truncate text-xs text-muted-foreground'>{booking.partnerName}</span>
                    </div>
                    <span className='hidden shrink-0 text-xs text-muted-foreground sm:inline'>
                      {formatDisplayDateTime(booking.createdAt)}
                    </span>
                    <span className='shrink-0 font-semibold tabular-nums'>{formatMoney(booking.revenue)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className='text-muted-foreground'>{t`No bookings yet.`}</p>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </NoddiCard>
  );
};

const describeBooking = (booking: BrandInsightsLiveBookingRecord) => {
  const services = booking.categories.map(translateSalesItemCategory).join(', ') || t`Booking`;
  return booking.city ? `${services} · ${booking.city}` : services;
};
